import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Calendar, CalendarDays, Copy, Info, LockKeyhole, Mail } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Check } from "@/components/animate-ui/icons/check";
import { CheckCheck } from "@/components/animate-ui/icons/check-check";
import { Clipboard } from "@/components/animate-ui/icons/clipboard";
import { Clock } from "@/components/animate-ui/icons/clock";
import { AnimateIcon } from "@/components/animate-ui/icons/icon";
import { Loader } from "@/components/animate-ui/icons/loader";
import { MessageSquareWarning } from "@/components/animate-ui/icons/message-square-warning";
import { PlugZap } from "@/components/animate-ui/icons/plug-zap";
import { SendHorizontal } from "@/components/animate-ui/icons/send-horizontal";
import { Sun } from "@/components/animate-ui/icons/sun";
import { TimezoneSelector } from "@/components/timezone-selector";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { WebhookCodeExamples } from "@/components/webhook-code-examples";
import { PERMISSION_MODES } from "@/constants/permissions";
import { getAppIntegrations, getUserIntegrations } from "@/lib/api/integrations";
import { integrationKeys } from "@/lib/api/query-keys/integrations";
import type { components } from "@/lib/api/types";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";

export interface ScheduleConfig {
	triggerType: "interval" | "cron";
	intervalSeconds?: number;
	cronExpression?: string;
	timezone?: string;
}

export interface WebhookConfig {
	enabled: boolean;
	hasToken: boolean;
	tokenPrefix?: string | null;
	maskedUrl?: string | null;
}

export interface WebhookRotateResult {
	webhookUrl: string;
	maskedUrl: string;
}

interface TaskConfirmationModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	instructions: string;
	onSubmit: (data: TaskFormData) => void;
	isSubmitting?: boolean;
	isEditMode?: boolean;
	defaultName?: string;
	defaultIntegrations?: number[];
	defaultGoals?: string;
	showInstructionsField?: boolean;
	defaultSchedule?: ScheduleConfig;
	defaultWebhook?: WebhookConfig;
	defaultPermissionMode?: TaskFormData["permissionMode"];
	showPermissionModeSelector?: boolean;
	requirePermissionModeSelection?: boolean;
	onRotateWebhook?: () => Promise<WebhookRotateResult>;
	/** Hide the Email Inbox toggle (only show in quick-create where teammate is also created). */
	hideEmailInboxSection?: boolean;
	/** Hide the "When should this run?" section (compact kanban create mode). */
	hideScheduleSection?: boolean;
	/** Show only schedule config (no Run now / Events from Apps options). */
	scheduleOnly?: boolean;
	/** Override submit button text (e.g. "Create" for kanban). */
	submitLabel?: string;
}

export interface TaskFormData {
	name: string;
	instructions: string;
	expectedOutput: string;
	goals: string;
	integrations: number[];
	emailNotifications: boolean;
	inboundEmailEnabled: boolean;
	permissionMode: "yolo" | "safe" | "plan";
	schedule: "now" | "interval" | "daily" | "weekly" | "monthly" | "external";
	// Interval fields
	intervalMinutes?: number;
	intervalUnit?: "minutes" | "hours";
	// Time fields (for daily/weekly/monthly)
	timeHour?: number;
	timeMinute?: number;
	timezone?: string;
	// Weekly fields
	daysOfWeek?: number[];
	// Monthly fields
	dayOfMonth?: number;
}

type UserIntegration = components["schemas"]["IntegrationResponse"];

const humanizeSegment = (value: string) =>
	value
		.split(/[\s_-]+/)
		.filter(Boolean)
		.map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
		.join(" ");

const RUN_LOADING_MESSAGES = [
	"Setting up mate...",
	"Preparing workspace...",
	"Starting task...",
	"Warming up...",
	"Getting ready...",
	"Loading tools...",
	"Almost there...",
	"Thinking...",
	"Spinning up...",
	"Initializing...",
];

const SCHEDULE_LOADING_MESSAGES = [
	"Setting up schedule...",
	"Configuring automation...",
	"Preparing schedule...",
	"Almost there...",
	"Getting ready...",
	"Setting things up...",
	"Registering schedule...",
	"Thinking...",
	"Finalizing...",
	"Saving configuration...",
];

function useRotatingMessage(messages: string[], active: boolean, intervalMs = 1500) {
	const [current, setCurrent] = useState(0);

	useEffect(() => {
		if (!active) return;
		setCurrent(Math.floor(Math.random() * messages.length));
		const id = setInterval(
			() =>
				setCurrent((prev) => {
					let next: number;
					do {
						next = Math.floor(Math.random() * messages.length);
					} while (next === prev && messages.length > 1);
					return next;
				}),
			intervalMs
		);
		return () => clearInterval(id);
	}, [active, messages, intervalMs]);

	return messages[current];
}

export function TaskConfirmationModal({
	open,
	onOpenChange,
	instructions,
	onSubmit,
	isSubmitting = false,
	isEditMode = false,
	defaultName = "",
	defaultIntegrations = [],
	defaultGoals = "",
	showInstructionsField = false,
	defaultSchedule,
	defaultWebhook,
	defaultPermissionMode = "safe",
	showPermissionModeSelector = false,
	requirePermissionModeSelection = false,
	onRotateWebhook,
	hideEmailInboxSection = false,
	hideScheduleSection = false,
	scheduleOnly = false,
	submitLabel,
}: TaskConfirmationModalProps) {
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const nameId = useId();
	const [name, setName] = useState(defaultName);
	const [editableInstructions, setEditableInstructions] = useState(instructions);
	const [goals, setGoals] = useState(defaultGoals);
	const [selectedIntegrations, setSelectedIntegrations] = useState<number[]>(
		defaultIntegrations.length ? [...defaultIntegrations] : []
	);
	const [visibleIntegrationCount, setVisibleIntegrationCount] = useState(3);
	const [emailNotifications, _setEmailNotifications] = useState(true);
	const [inboundEmailEnabled, setInboundEmailEnabled] = useState(false);
	const [permissionMode, setPermissionMode] = useState<TaskFormData["permissionMode"] | "">(
		defaultPermissionMode
	);

	// Track modal open transition to avoid resetting state on prop changes while open
	const wasOpenRef = useRef(false);

	// Execution type: top-level choice (3 options)
	const [executionType, setExecutionType] = useState<"now" | "scheduled" | "external">("now");

	// Schedule type: sub-level choice (only when executionType === "scheduled")
	const [scheduleType, setScheduleType] = useState<"interval" | "daily" | "weekly" | "monthly">(
		"interval"
	);

	// Interval state
	const [intervalValue, setIntervalValue] = useState(30);
	const [intervalUnit, setIntervalUnit] = useState<"minutes" | "hours">("minutes");

	// Time state (for daily/weekly/monthly)
	const [timeHour, setTimeHour] = useState(9);
	const [timeMinute, setTimeMinute] = useState(0);

	// Timezone state (auto-detected by TimezoneSelector component)
	const [timezone, setTimezone] = useState("America/Los_Angeles");

	// Weekly state
	const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1, 2, 3, 4, 5]); // Mon-Fri

	// Monthly state
	const [dayOfMonth, setDayOfMonth] = useState(1);

	// Validation state
	const [intervalError, setIntervalError] = useState<string | null>(null);
	const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
	const [isRotatingWebhook, setIsRotatingWebhook] = useState(false);

	// Rotating loading message
	const loadingMessage = useRotatingMessage(
		executionType === "now" ? RUN_LOADING_MESSAGES : SCHEDULE_LOADING_MESSAGES,
		isSubmitting
	);

	// Generate unique IDs for form fields
	const instructionsId = useId();
	const goalsId = useId();
	const intervalId = useId();
	const timeId = useId();

	// Fetch available integrations from API
	const { data: integrationsData, isLoading: isLoadingIntegrationsRaw } = useQuery<
		UserIntegration[]
	>({
		queryKey: integrationKeys.user(),
		queryFn: getUserIntegrations,
		enabled: open && isAuthenticated, // Only fetch when modal is open and user is authenticated
		staleTime: 60 * 1000,
	});
	const integrations = open && isAuthenticated ? (integrationsData ?? []) : [];
	const isLoadingIntegrations = open && isAuthenticated ? isLoadingIntegrationsRaw : false;

	const { data: appIntegrations = [] } = useQuery({
		queryKey: integrationKeys.catalog(),
		queryFn: getAppIntegrations,
		staleTime: 5 * 60 * 1000,
	});

	const integrationNameMap = useMemo(() => {
		const map = new Map<number, string>();
		for (const integration of appIntegrations ?? []) {
			map.set(integration.id, integration.name);
		}
		return map;
	}, [appIntegrations]);

	// Sync instructions, goals, name, default integrations, and schedule when modal opens
	useEffect(() => {
		if (!open) {
			wasOpenRef.current = false;
			return;
		}
		if (wasOpenRef.current) return; // Already open — don't reset on prop changes
		wasOpenRef.current = true;

		setName(defaultName);
		setEditableInstructions(instructions);
		setGoals(defaultGoals);
		setSelectedIntegrations(defaultIntegrations.length ? [...defaultIntegrations] : []);
		setVisibleIntegrationCount(3);
		setPermissionMode(defaultPermissionMode);

		// Restore schedule state if editing a scheduled task
		if (defaultSchedule) {
			setExecutionType("scheduled");

			if (defaultSchedule.triggerType === "interval" && defaultSchedule.intervalSeconds) {
				setScheduleType("interval");
				// Convert seconds to minutes or hours
				const totalMinutes = Math.floor(defaultSchedule.intervalSeconds / 60);
				if (totalMinutes >= 60 && totalMinutes % 60 === 0) {
					setIntervalValue(Math.floor(totalMinutes / 60));
					setIntervalUnit("hours");
				} else {
					setIntervalValue(totalMinutes);
					setIntervalUnit("minutes");
				}
			} else if (defaultSchedule.triggerType === "cron" && defaultSchedule.cronExpression) {
				// Parse cron expression: "minute hour day month day_of_week"
				const parts = defaultSchedule.cronExpression.split(" ");
				if (parts.length === 5) {
					const [minute, hour, day, , dayOfWeek] = parts;
					setTimeMinute(Number.parseInt(minute, 10) || 0);
					setTimeHour(Number.parseInt(hour, 10) || 9);

					// Determine schedule type from cron
					if (dayOfWeek !== "*" && day === "*") {
						// Weekly: specific day(s) of week
						setScheduleType("weekly");
						const days = dayOfWeek.split(",").map((d) => Number.parseInt(d, 10));
						setDaysOfWeek(days);
					} else if (day !== "*" && dayOfWeek === "*") {
						// Monthly: specific day of month
						setScheduleType("monthly");
						setDayOfMonth(Number.parseInt(day, 10) || 1);
					} else {
						// Daily: all days
						setScheduleType("daily");
					}
				}
			}

			if (defaultSchedule.timezone) {
				setTimezone(defaultSchedule.timezone);
			}
		} else if (scheduleOnly) {
			setExecutionType("scheduled");
			setScheduleType("daily");
		} else if (defaultWebhook?.enabled) {
			setExecutionType("external");
		} else {
			// Reset to defaults when not editing a schedule
			setExecutionType("now");
			setScheduleType("interval");
		}

		setWebhookUrl(null);
	}, [
		open,
		defaultName,
		instructions,
		defaultGoals,
		defaultIntegrations,
		defaultSchedule,
		defaultWebhook,
		defaultPermissionMode,
		scheduleOnly,
	]);

	const submitForm = () => {
		if (isSubmitting) {
			return;
		}

		// Validate weekly schedule has at least one day selected
		if (executionType === "scheduled" && scheduleType === "weekly" && daysOfWeek.length === 0) {
			toast.error("Please select at least one day for the weekly schedule");
			return;
		}

		// Validate interval value is at least minimum
		if (executionType === "scheduled" && scheduleType === "interval") {
			const minValue = intervalUnit === "minutes" ? 5 : 1;
			if (intervalValue < minValue) {
				toast.error(
					`Interval must be at least ${minValue} ${intervalUnit === "minutes" ? "minutes" : "hour"}`
				);
				return;
			}
		}

		if (showPermissionModeSelector && requirePermissionModeSelection && !permissionMode) {
			toast.error("Please choose an approval mode before continuing");
			return;
		}

		// Compute backend schedule value from executionType + scheduleType
		const schedule =
			executionType === "now"
				? "now"
				: executionType === "external"
					? "external"
					: (scheduleType as typeof scheduleType);

		onSubmit({
			name,
			instructions: editableInstructions,
			expectedOutput: "", // Hidden for now
			goals,
			integrations: selectedIntegrations,
			emailNotifications,
			inboundEmailEnabled,
			permissionMode: (permissionMode || defaultPermissionMode) as TaskFormData["permissionMode"],
			schedule,
			// Interval fields
			intervalMinutes:
				executionType === "scheduled" && scheduleType === "interval" ? intervalValue : undefined,
			intervalUnit:
				executionType === "scheduled" && scheduleType === "interval" ? intervalUnit : undefined,
			// Time fields (for daily/weekly/monthly)
			timeHour:
				executionType === "scheduled" &&
				(scheduleType === "daily" || scheduleType === "weekly" || scheduleType === "monthly")
					? timeHour
					: undefined,
			timeMinute:
				executionType === "scheduled" &&
				(scheduleType === "daily" || scheduleType === "weekly" || scheduleType === "monthly")
					? timeMinute
					: undefined,
			timezone:
				executionType === "scheduled" &&
				(scheduleType === "daily" || scheduleType === "weekly" || scheduleType === "monthly")
					? timezone
					: undefined,
			// Weekly fields
			daysOfWeek:
				executionType === "scheduled" && scheduleType === "weekly" ? daysOfWeek : undefined,
			// Monthly fields
			dayOfMonth:
				executionType === "scheduled" && scheduleType === "monthly" ? dayOfMonth : undefined,
		});
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		submitForm();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
		if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
			e.preventDefault();
			submitForm();
		}
	};

	const toggleIntegration = (integrationId: number) => {
		setSelectedIntegrations((prev) =>
			prev.includes(integrationId)
				? prev.filter((id) => id !== integrationId)
				: [...prev, integrationId]
		);
	};

	const displayWebhookValue =
		webhookUrl ||
		defaultWebhook?.maskedUrl ||
		(defaultWebhook?.hasToken && defaultWebhook?.tokenPrefix
			? `Token ${defaultWebhook.tokenPrefix}****`
			: null);

	return (
		<Dialog open={open} onOpenChange={isSubmitting ? undefined : onOpenChange}>
			<DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-4 sm:p-6 top-[15vh] translate-y-0">
				<DialogHeader className="shrink-0">
					<DialogTitle>
						{isEditMode
							? "Edit Task"
							: showInstructionsField && instructions
								? "Re-run Task"
								: "Create Task"}
					</DialogTitle>
				</DialogHeader>

				<form
					onSubmit={handleSubmit}
					onKeyDown={handleKeyDown}
					className="relative flex flex-col flex-1 min-h-0"
				>
					{isSubmitting && (
						<div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/80 backdrop-blur-xs rounded-md">
							<Loader className="size-6 animate-spin text-muted-foreground" />
							<p className="mt-3 text-sm text-muted-foreground">{loadingMessage}</p>
						</div>
					)}
					<div className="space-y-4 sm:space-y-6 overflow-y-auto flex-1 px-1">
						{/* Task Name - Only show in edit mode */}
						{isEditMode && (
							<div className="space-y-2">
								<Label htmlFor={nameId} className="text-sm font-medium">
									Task Name
								</Label>
								<Input
									id={nameId}
									placeholder="E.g., Weekly sales report"
									value={name}
									onChange={(e) => setName(e.target.value)}
									className="text-sm"
									required
								/>
							</div>
						)}

						{/* Task Instructions - Show when showInstructionsField is true */}
						{showInstructionsField && (
							<div className="space-y-2">
								<div className="flex items-center gap-2">
									<Clipboard className="size-4 text-muted-foreground" animateOnHover />
									<Label htmlFor={instructionsId} className="text-sm font-medium">
										Task Instructions
									</Label>
								</div>
								<Textarea
									id={instructionsId}
									placeholder="Describe the task you want to automate..."
									value={editableInstructions}
									onChange={(e) => setEditableInstructions(e.target.value)}
									rows={3}
									className="resize-y text-base md:text-sm"
								/>
							</div>
						)}

						{/* Outcome */}
						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<CheckCheck className="size-4 text-muted-foreground" animateOnHover />
								<Label htmlFor={goalsId} className="text-sm font-medium">
									Outcome <span className="text-muted-foreground font-normal">(Optional)</span>
								</Label>
							</div>
							<Textarea
								id={goalsId}
								placeholder="Example: Analyze campaign performance and provide actionable recommendations for budget optimization"
								value={goals}
								onChange={(e) => setGoals(e.target.value)}
								rows={2}
								className="resize-y text-base md:text-sm"
							/>
						</div>

						{/* Apps */}
						<div className="space-y-3">
							<div className="flex items-center gap-2">
								<PlugZap className="size-4 text-muted-foreground" animateOnHover />
								<Label className="text-sm font-medium">Apps</Label>
							</div>

							{isLoadingIntegrations ? (
								<div className="text-sm text-muted-foreground">Loading integrations...</div>
							) : !isAuthenticated ? (
								<div className="text-sm text-muted-foreground">
									<Link to="/login" className="hover:text-foreground hover:underline">
										Log in
									</Link>{" "}
									to set up your applications.
								</div>
							) : integrations.length === 0 ? (
								<div className="space-y-1">
									<div className="text-sm text-muted-foreground">No connected apps.</div>
									<div className="text-xs text-muted-foreground">
										<Link
											to="/integrations"
											search={{
												oauth: undefined,
												provider: undefined,
												message: undefined,
												composio_connection_id: undefined,
												composio_app_integration_id: undefined,
											}}
											className="hover:text-foreground hover:underline"
										>
											See all apps.
										</Link>
									</div>
								</div>
							) : (
								<div className="space-y-2">
									{[...integrations]
										.sort((a, b) => {
											const aSelected = selectedIntegrations.includes(Number(a.app_integration_id));
											const bSelected = selectedIntegrations.includes(Number(b.app_integration_id));
											if (aSelected && !bSelected) return -1;
											if (!aSelected && bSelected) return 1;
											return 0;
										})
										.slice(0, visibleIntegrationCount)
										.map((integration) => {
											const appIntegrationId = Number(integration.app_integration_id);
											return (
												<div key={integration.id} className="flex items-center space-x-2">
													<Checkbox
														id={`integration-${integration.id}`}
														checked={selectedIntegrations.includes(appIntegrationId)}
														onCheckedChange={() => toggleIntegration(appIntegrationId)}
													/>
													<label
														htmlFor={`integration-${integration.id}`}
														className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
													>
														{integrationNameMap.get(appIntegrationId) ??
															[
																humanizeSegment(integration.provider),
																humanizeSegment(integration.kind),
															]
																.filter(Boolean)
																.join(" ")}
													</label>
												</div>
											);
										})}
									<div className="flex items-center space-x-2 text-xs text-muted-foreground">
										<span aria-hidden className="inline-block size-4" />
										{integrations.length > visibleIntegrationCount && (
											<button
												type="button"
												onClick={() => setVisibleIntegrationCount((prev) => prev + 5)}
												className="hover:text-foreground hover:underline"
											>
												+{integrations.length - visibleIntegrationCount} more
											</button>
										)}
										{integrations.length > visibleIntegrationCount && (
											<span aria-hidden>&middot;</span>
										)}
										<Link
											to="/integrations"
											search={{
												oauth: undefined,
												provider: undefined,
												message: undefined,
												composio_connection_id: undefined,
												composio_app_integration_id: undefined,
											}}
											className="hover:text-foreground hover:underline"
										>
											See all apps.
										</Link>
									</div>
								</div>
							)}
						</div>

						{/* Email Inbox — only in quick-create where teammate is also created */}
						{!hideEmailInboxSection && (
							<div className="space-y-2">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<Mail className="size-4 text-muted-foreground" />
										<Label className="text-sm font-medium">Email Inbox</Label>
									</div>
									<Switch checked={inboundEmailEnabled} onCheckedChange={setInboundEmailEnabled} />
								</div>
								{inboundEmailEnabled && (
									<p className="text-xs text-muted-foreground">
										A unique email inbox will be created after the teammate is set up.
									</p>
								)}
							</div>
						)}

						{/* Schedule — hidden in compact kanban create mode */}
						{!hideScheduleSection && (
							<div className="space-y-3">
								<div className="flex items-center gap-2">
									<Clock className="size-4 text-muted-foreground" animateOnHover />
									<Label className="text-sm font-medium">
										{scheduleOnly ? "Schedule" : "When should this run?"}
									</Label>
								</div>
								<div className="space-y-2">
									{!scheduleOnly && (
										<>
											{/* Run now */}
											<button
												type="button"
												onClick={() => setExecutionType("now")}
												className={cn(
													"flex items-center space-x-3 rounded-lg border p-4 cursor-pointer transition-colors w-full text-left",
													executionType === "now"
														? "border-primary bg-primary/5"
														: "border-border hover:bg-accent"
												)}
											>
												<div
													className={cn(
														"flex h-4 w-4 items-center justify-center rounded-full border",
														executionType === "now"
															? "border-primary bg-primary"
															: "border-muted-foreground"
													)}
												>
													{executionType === "now" && (
														<Check className="h-2.5 w-2.5 text-primary-foreground" animateOnHover />
													)}
												</div>
												<Tooltip delayDuration={300}>
													<TooltipTrigger asChild>
														<div className="flex-1">
															<div className="text-sm font-medium">
																{isEditMode ? "On Demand" : "Run now"}
															</div>
														</div>
													</TooltipTrigger>
													<TooltipContent variant="subtle">
														Executes immediately, one time
													</TooltipContent>
												</Tooltip>
											</button>

											{/* Schedule */}
											<button
												type="button"
												onClick={() => setExecutionType("scheduled")}
												className={cn(
													"flex items-center space-x-3 rounded-lg border p-4 cursor-pointer transition-colors w-full text-left",
													executionType === "scheduled"
														? "border-primary bg-primary/5"
														: "border-border hover:bg-accent"
												)}
											>
												<div
													className={cn(
														"flex h-4 w-4 items-center justify-center rounded-full border",
														executionType === "scheduled"
															? "border-primary bg-primary"
															: "border-muted-foreground"
													)}
												>
													{executionType === "scheduled" && (
														<Check className="h-2.5 w-2.5 text-primary-foreground" animateOnHover />
													)}
												</div>
												<Tooltip delayDuration={300}>
													<TooltipTrigger asChild>
														<div className="flex-1">
															<div className="text-sm font-medium">On a Schedule</div>
														</div>
													</TooltipTrigger>
													<TooltipContent variant="subtle">
														Runs automatically on a recurring schedule
													</TooltipContent>
												</Tooltip>
											</button>

											{/* Events from your apps */}
											<button
												type="button"
												onClick={() => setExecutionType("external")}
												className={cn(
													"flex items-center space-x-3 rounded-lg border p-4 cursor-pointer transition-colors w-full text-left",
													executionType === "external"
														? "border-primary bg-primary/5"
														: "border-border hover:bg-accent"
												)}
											>
												<div
													className={cn(
														"flex h-4 w-4 items-center justify-center rounded-full border",
														executionType === "external"
															? "border-primary bg-primary"
															: "border-muted-foreground"
													)}
												>
													{executionType === "external" && (
														<Check className="h-2.5 w-2.5 text-primary-foreground" animateOnHover />
													)}
												</div>
												<div className="flex-1">
													<div className="text-sm font-medium">Events from Apps</div>
												</div>
											</button>
										</>
									)}

									{executionType === "scheduled" && (
										<div className="mt-3 rounded-lg border border-border/50 bg-accent/30 p-4 space-y-4">
											{/* Schedule type tabs */}
											<Tabs
												value={scheduleType}
												onValueChange={(value) =>
													setScheduleType(value as "interval" | "daily" | "weekly" | "monthly")
												}
											>
												<TabsList className="w-full grid grid-cols-2 sm:grid-cols-4 gap-1">
													<Tooltip delayDuration={300}>
														<TooltipTrigger asChild>
															<TabsTrigger
																value="interval"
																className="gap-1 sm:gap-1.5 text-xs sm:text-sm"
															>
																<Clock
																	className="size-3.5 sm:size-4"
																	aria-hidden="true"
																	animateOnHover
																/>
																Interval
															</TabsTrigger>
														</TooltipTrigger>
														<TooltipContent variant="subtle">
															Repeat every N minutes/hours
														</TooltipContent>
													</Tooltip>
													<Tooltip delayDuration={300}>
														<TooltipTrigger asChild>
															<TabsTrigger
																value="daily"
																className="gap-1 sm:gap-1.5 text-xs sm:text-sm"
															>
																<Sun
																	className="size-3.5 sm:size-4"
																	aria-hidden="true"
																	animateOnHover
																/>
																Daily
															</TabsTrigger>
														</TooltipTrigger>
														<TooltipContent variant="subtle">
															Once per day at a set time
														</TooltipContent>
													</Tooltip>
													<Tooltip delayDuration={300}>
														<TooltipTrigger asChild>
															<TabsTrigger
																value="weekly"
																className="gap-1 sm:gap-1.5 text-xs sm:text-sm"
															>
																<CalendarDays className="size-3.5 sm:size-4" aria-hidden="true" />
																Weekly
															</TabsTrigger>
														</TooltipTrigger>
														<TooltipContent variant="subtle">
															On specific days each week
														</TooltipContent>
													</Tooltip>
													<Tooltip delayDuration={300}>
														<TooltipTrigger asChild>
															<TabsTrigger
																value="monthly"
																className="gap-1 sm:gap-1.5 text-xs sm:text-sm"
															>
																<Calendar className="size-3.5 sm:size-4" aria-hidden="true" />
																Monthly
															</TabsTrigger>
														</TooltipTrigger>
														<TooltipContent variant="subtle">
															On a specific day each month
														</TooltipContent>
													</Tooltip>
												</TabsList>
											</Tabs>

											{/* Interval configuration */}
											{scheduleType === "interval" && (
												<div className="space-y-2">
													<Label htmlFor={intervalId} className="text-sm">
														Run every
													</Label>
													<div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
														<Input
															id={intervalId}
															type="number"
															min={intervalUnit === "minutes" ? "5" : "1"}
															max={intervalUnit === "minutes" ? "1440" : "168"}
															value={intervalValue}
															onChange={(e) => {
																const newValue = Number.parseInt(e.target.value, 10);
																setIntervalValue(newValue);

																// Validate and set error
																const minValue = intervalUnit === "minutes" ? 5 : 1;
																const maxValue = intervalUnit === "minutes" ? 1440 : 168;
																if (newValue < minValue) {
																	setIntervalError(
																		`Minimum: ${minValue} ${
																			intervalUnit === "minutes" ? "minutes" : "hour"
																		}`
																	);
																} else if (newValue > maxValue) {
																	setIntervalError(
																		`Maximum: ${maxValue} ${
																			intervalUnit === "minutes" ? "minutes" : "hours"
																		} (${intervalUnit === "minutes" ? "24 hours" : "7 days"})`
																	);
																} else {
																	setIntervalError(null);
																}
															}}
															className="w-20 sm:w-24"
															aria-invalid={intervalError !== null}
														/>
														<Select
															value={intervalUnit}
															onValueChange={(value: "minutes" | "hours") => {
																setIntervalUnit(value);
																// Adjust value if below minimum for new unit
																let newValue = intervalValue;
																if (value === "minutes" && intervalValue < 5) {
																	newValue = 5;
																	setIntervalValue(5);
																}

																// Revalidate with new unit
																const minValue = value === "minutes" ? 5 : 1;
																const maxValue = value === "minutes" ? 1440 : 168;
																if (newValue < minValue) {
																	setIntervalError(
																		`Minimum: ${minValue} ${value === "minutes" ? "minutes" : "hour"}`
																	);
																} else if (newValue > maxValue) {
																	setIntervalError(
																		`Maximum: ${maxValue} ${
																			value === "minutes" ? "minutes" : "hours"
																		} (${value === "minutes" ? "24 hours" : "7 days"})`
																	);
																} else {
																	setIntervalError(null);
																}
															}}
														>
															<SelectTrigger className="w-24 sm:w-[120px]">
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																<SelectItem value="minutes">minutes</SelectItem>
																<SelectItem value="hours">hours</SelectItem>
															</SelectContent>
														</Select>
													</div>
													{intervalError && (
														<p className="text-xs text-red-600 dark:text-red-400">
															{intervalError}
														</p>
													)}
												</div>
											)}

											{/* Daily configuration */}
											{scheduleType === "daily" && (
												<div className="space-y-4">
													<TimezoneSelector
														value={timezone}
														onChange={setTimezone}
														label="Timezone"
														showCurrentTime={true}
													/>
													<div className="space-y-2">
														<Label htmlFor={timeId} className="text-sm">
															Time
														</Label>
														<div className="flex items-center gap-2">
															<Select
																value={timeHour.toString()}
																onValueChange={(value) => setTimeHour(Number.parseInt(value, 10))}
															>
																<SelectTrigger className="w-20 sm:w-[100px]" aria-label="Hour">
																	<SelectValue />
																</SelectTrigger>
																<SelectContent>
																	{Array.from({ length: 24 }, (_, i) => (
																		<SelectItem key={i.toString()} value={i.toString()}>
																			{i.toString().padStart(2, "0")}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
															<span className="text-sm text-muted-foreground">:</span>
															<Select
																value={timeMinute.toString()}
																onValueChange={(value) => setTimeMinute(Number.parseInt(value, 10))}
															>
																<SelectTrigger className="w-20 sm:w-[100px]" aria-label="Minute">
																	<SelectValue />
																</SelectTrigger>
																<SelectContent>
																	{Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => (
																		<SelectItem key={minute.toString()} value={minute.toString()}>
																			{minute.toString().padStart(2, "0")}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
														</div>
													</div>
												</div>
											)}

											{/* Weekly configuration */}
											{scheduleType === "weekly" && (
												<div className="space-y-3">
													<div className="space-y-2">
														<Label className="text-sm">Days</Label>
														<div className="flex flex-wrap gap-2">
															{[
																{ day: 1, label: "Mon" },
																{ day: 2, label: "Tue" },
																{ day: 3, label: "Wed" },
																{ day: 4, label: "Thu" },
																{ day: 5, label: "Fri" },
																{ day: 6, label: "Sat" },
																{ day: 0, label: "Sun" },
															].map(({ day, label }) => (
																<button
																	key={day}
																	type="button"
																	aria-pressed={daysOfWeek.includes(day)}
																	onClick={() => {
																		setDaysOfWeek((prev) =>
																			prev.includes(day)
																				? prev.filter((d) => d !== day)
																				: [...prev, day].sort()
																		);
																	}}
																	className={cn(
																		"px-3 py-1.5 text-sm rounded-md border transition-colors",
																		daysOfWeek.includes(day)
																			? "border-primary bg-primary/10 text-primary font-medium"
																			: "border-border hover:bg-accent"
																	)}
																>
																	{label}
																</button>
															))}
														</div>
													</div>
													<TimezoneSelector
														value={timezone}
														onChange={setTimezone}
														label="Timezone"
														showCurrentTime={true}
													/>
													<div className="space-y-2">
														<Label htmlFor={timeId} className="text-sm">
															Time
														</Label>
														<div className="flex items-center gap-2">
															<Select
																value={timeHour.toString()}
																onValueChange={(value) => setTimeHour(Number.parseInt(value, 10))}
															>
																<SelectTrigger className="w-20 sm:w-[100px]" aria-label="Hour">
																	<SelectValue />
																</SelectTrigger>
																<SelectContent>
																	{Array.from({ length: 24 }, (_, i) => (
																		<SelectItem key={i.toString()} value={i.toString()}>
																			{i.toString().padStart(2, "0")}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
															<span className="text-sm text-muted-foreground">:</span>
															<Select
																value={timeMinute.toString()}
																onValueChange={(value) => setTimeMinute(Number.parseInt(value, 10))}
															>
																<SelectTrigger className="w-20 sm:w-[100px]" aria-label="Minute">
																	<SelectValue />
																</SelectTrigger>
																<SelectContent>
																	{Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => (
																		<SelectItem key={minute.toString()} value={minute.toString()}>
																			{minute.toString().padStart(2, "0")}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
														</div>
													</div>
												</div>
											)}

											{/* Monthly configuration */}
											{scheduleType === "monthly" && (
												<div className="space-y-3">
													<div className="space-y-2">
														<Label className="text-sm">Day of month</Label>
														<Select
															value={dayOfMonth.toString()}
															onValueChange={(value) => setDayOfMonth(Number.parseInt(value, 10))}
														>
															<SelectTrigger className="w-24 sm:w-[120px]">
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																{Array.from({ length: 31 }, (_, i) => (
																	<SelectItem key={(i + 1).toString()} value={(i + 1).toString()}>
																		{i + 1}
																		{i + 1 === 1
																			? "st"
																			: i + 1 === 2
																				? "nd"
																				: i + 1 === 3
																					? "rd"
																					: "th"}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														{dayOfMonth > 28 && (
															<div className="mt-2 p-2.5 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md">
																<p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
																	<MessageSquareWarning className="size-3.5 shrink-0 mt-0.5" />
																	<span>
																		Day {dayOfMonth} will skip months with fewer days (e.g.,
																		February
																		{dayOfMonth === 31 && ", April, June, September, November"})
																	</span>
																</p>
															</div>
														)}
													</div>
													<TimezoneSelector
														value={timezone}
														onChange={setTimezone}
														label="Timezone"
														showCurrentTime={true}
													/>
													<div className="space-y-2">
														<Label htmlFor={timeId} className="text-sm">
															Time
														</Label>
														<div className="flex items-center gap-2">
															<Select
																value={timeHour.toString()}
																onValueChange={(value) => setTimeHour(Number.parseInt(value, 10))}
															>
																<SelectTrigger className="w-20 sm:w-[100px]" aria-label="Hour">
																	<SelectValue />
																</SelectTrigger>
																<SelectContent>
																	{Array.from({ length: 24 }, (_, i) => (
																		<SelectItem key={i.toString()} value={i.toString()}>
																			{i.toString().padStart(2, "0")}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
															<span className="text-sm text-muted-foreground">:</span>
															<Select
																value={timeMinute.toString()}
																onValueChange={(value) => setTimeMinute(Number.parseInt(value, 10))}
															>
																<SelectTrigger className="w-20 sm:w-[100px]" aria-label="Minute">
																	<SelectValue />
																</SelectTrigger>
																<SelectContent>
																	{Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => (
																		<SelectItem key={minute.toString()} value={minute.toString()}>
																			{minute.toString().padStart(2, "0")}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
														</div>
													</div>
												</div>
											)}
										</div>
									)}

									{executionType === "external" && (
										<div className="mt-3 rounded-lg border border-border/50 bg-accent/30 p-4 space-y-3">
											<div className="rounded-md border bg-background p-3">
												<div className="flex items-center gap-1.5">
													<div className="text-sm font-medium">Webhook</div>
													<Tooltip>
														<TooltipTrigger asChild>
															<Info className="size-3.5 text-muted-foreground cursor-help" />
														</TooltipTrigger>
														<TooltipContent side="top" className="max-w-xs">
															Make a POST request to this URL with an optional JSON payload to
															trigger this task.
														</TooltipContent>
													</Tooltip>
												</div>
												{displayWebhookValue && (
													<div className="mt-2 flex items-center gap-1 rounded-md border px-3 py-2 bg-muted/50">
														<code className="text-xs flex-1 truncate">{displayWebhookValue}</code>
														{webhookUrl && (
															<Tooltip>
																<TooltipTrigger asChild>
																	<Button
																		type="button"
																		variant="ghost"
																		size="icon"
																		className="h-7 w-7 shrink-0"
																		onClick={() => {
																			navigator.clipboard.writeText(webhookUrl);
																			toast.success("Webhook URL copied");
																		}}
																	>
																		<Copy className="h-3.5 w-3.5" />
																		<span className="sr-only">Copy webhook URL</span>
																	</Button>
																</TooltipTrigger>
																<TooltipContent>Copy URL</TooltipContent>
															</Tooltip>
														)}
													</div>
												)}
												{isEditMode && onRotateWebhook && (
													<Button
														type="button"
														variant="outline"
														size="sm"
														className="mt-3"
														disabled={isRotatingWebhook}
														onClick={async () => {
															setIsRotatingWebhook(true);
															try {
																const result = await onRotateWebhook();
																setWebhookUrl(result.webhookUrl);
																toast.success(
																	defaultWebhook?.hasToken
																		? "Webhook URL rotated"
																		: "Webhook URL created"
																);
															} catch {
																toast.error("Failed to rotate webhook URL");
															} finally {
																setIsRotatingWebhook(false);
															}
														}}
													>
														{defaultWebhook?.hasToken
															? isRotatingWebhook
																? "Rotating..."
																: "Rotate URL"
															: isRotatingWebhook
																? "Creating..."
																: "Create Webhook URL"}
													</Button>
												)}
											</div>
											{(webhookUrl || displayWebhookValue) && (
												<WebhookCodeExamples
													url={webhookUrl ?? displayWebhookValue ?? "<webhook-url>"}
													bodyJson='{"key": "value"}'
												/>
											)}
										</div>
									)}
								</div>
							</div>
						)}

						{showPermissionModeSelector && (
							<div className="space-y-3">
								<div className="flex items-center gap-2">
									<LockKeyhole className="size-4 text-muted-foreground" />
									<Label className="text-sm font-medium">Permissions</Label>
								</div>
								<Select
									value={permissionMode}
									onValueChange={(value) =>
										setPermissionMode(value as TaskFormData["permissionMode"])
									}
								>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="Select approval mode" />
									</SelectTrigger>
									<SelectContent>
										{PERMISSION_MODES.map((mode) => (
											<SelectItem key={mode.value} value={mode.value}>
												<mode.icon className="size-4 text-muted-foreground" />
												{mode.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}
					</div>

					{/* Footer */}
					<DialogFooter className="shrink-0 pt-4 border-t mt-4 gap-3">
						<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
							<kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
								<span className="text-xs">⌘</span>↵
							</kbd>
							<span>{isEditMode ? "to save" : "to submit"}</span>
						</div>
						<AnimateIcon animateOnHover asChild>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting && <Loader className="mr-2 size-4 animate-spin" />}
								{isSubmitting
									? isEditMode
										? "Saving..."
										: submitLabel
											? "Creating..."
											: executionType === "now"
												? "Running..."
												: executionType === "scheduled"
													? "Scheduling..."
													: "Configuring..."
									: submitLabel
										? submitLabel
										: isEditMode
											? "Save Changes"
											: executionType === "now"
												? "Run Task"
												: executionType === "scheduled"
													? "Schedule Task"
													: "Save Webhook"}
								<SendHorizontal className="size-4" />
							</Button>
						</AnimateIcon>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
